import React, { useEffect, useState } from "react";
import {
  Textarea,
  Input,
  Switch,
  Button,
  Slider,
  Select,
  SelectItem,
  Spinner,
} from "@heroui/react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { AgentConfigInput } from "@webuild-ai/admin/models/components";
import { LLMModel, Tool } from "@webuild-ai/agent-platform/models/components";

import { useWorkflowStore } from "@/stores/WorkflowStore";
import { useChatStore } from "@/stores/ChatStore";
import { useWebuildAPIClient } from "@/providers/WeBuildClientProvider";
import { logger } from "@/telemetry";

interface WorkflowSettingsProps {
  isOpen: boolean;
}

export const WorkflowSettings: React.FC<WorkflowSettingsProps> = ({ isOpen }) => {
  const { workflow, setWorkflow, updateAgentTools } = useWorkflowStore();
  const { runId } = useChatStore();

  const { platformClient } = useWebuildAPIClient();

  const [models, setModels] = useState<LLMModel[]>([]);
  const [available_tools, setAvailableTools] = useState<Tool[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!platformClient) {
      throw new Error("Platform client not found");
    }
    const fetchModels = async () => {
      try {
        const models = await platformClient.models.list();
        const tools = await platformClient.tools.list();

        setAvailableTools(tools);
        setModels(models);
      } catch (error) {
        logger.error("Failed to fetch models or tools", error);
      } finally {
        setIsLoading(false);
      }
    };

    setIsLoading(true);
    fetchModels();
  }, [platformClient, isOpen]);

  const updateAgentParameter = (agent_id: string, param: string, value: string) => {
    if (!workflow) return;

    const updatedWorkflow = {
      ...workflow,
      agents: {
        ...workflow.agents,
        [agent_id]: {
          ...workflow.agents[agent_id],
          parameters: {
            ...workflow.agents[agent_id].parameters,
            [param]: value,
          },
        },
      },
    };

    setWorkflow(updatedWorkflow);
  };

  const updateAgentParameters = (agent_id: string, paramsToUpdate: Record<string, any>) => {
    if (!workflow) return;

    const updatedWorkflow = {
      ...workflow,
      agents: {
        ...workflow.agents,
        [agent_id]: {
          ...workflow.agents[agent_id],
          parameters: {
            ...workflow.agents[agent_id].parameters,
            ...paramsToUpdate,
          },
        },
      },
    };

    setWorkflow(updatedWorkflow);
  };

  const renderTools = (agent_id: string, agent: AgentConfigInput) => {
    return (
      <div className="flex flex-col gap-2 pt-2 pb-2" data-id="tools-section">
        <span className="text-small text-default-500">Tools</span>
        <div className="flex flex-wrap gap-2">
          {available_tools.map((tool) => {
            const tools = agent.parameters?.tools;
            const isSelected = Array.isArray(tools) && tools.includes(tool.id);

            return (
              <Button
                key={`${agent_id}-tool-${tool.id}`}
                className="text-xs w-40 justify-start"
                color={isSelected ? "primary" : "default"}
                data-id={`tool-${tool.id}`}
                isDisabled={!!runId}
                size="sm"
                startContent={<Icon icon={tool.icon} />}
                onPress={() => updateAgentTools(agent_id, tool.id, !isSelected)}
              >
                {tool.name}
              </Button>
            );
          })}
        </div>
      </div>
    );
  };

  const renderParameters = (agent_id: string, agent: AgentConfigInput) => {
    const parameters = Object.entries(agent.parameters).map(([param, value], index) => {
      const label = param.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
      const uniqueKey = `${agent_id}-param-${param}-${index}`;

      if (param === "temperature") {
        const parsedValue = parseFloat(String(value));

        return (
          <Slider
            key={uniqueKey}
            className="max-w-md"
            data-id={`param-${param}-slider`}
            defaultValue={parsedValue}
            isDisabled={!!runId}
            label={label}
            maxValue={1}
            minValue={0}
            showSteps={true}
            step={0.1}
            onChange={(val) => updateAgentParameter(agent_id, param, String(val))}
          />
        );
      }

      if (param === "model") {
        const modelExists = models.some((m) => m.id === value);
        const isDisabled = !!runId || models.length === 0;
        const selectedKeys = value && modelExists ? [String(value)] : [];

        return (
          <div key={uniqueKey} className="relative">
            <Select
              className="z-50"
              data-id={`param-${param}-select`}
              isDisabled={isDisabled}
              items={models}
              label={label}
              placeholder={models.length === 0 ? "Loading models..." : "Select a model"}
              popoverProps={{
                classNames: {
                  content: "z-50 bg-white border border-gray-200 shadow-lg min-w-[200px]",
                },
                shouldCloseOnBlur: false,
                shouldCloseOnInteractOutside: (element) => {
                  // Don't close if clicking on the Select component itself
                  const isSelectElement = element.closest(`[data-id="${param}-${param}-select"]`);
                  const isSelectButton =
                    element.hasAttribute("data-slot") &&
                    element.getAttribute("data-slot") === "trigger";
                  const isSelectIcon = element.closest('[data-slot="selectorIcon"]');

                  return !isSelectElement && !isSelectButton && !isSelectIcon;
                },
              }}
              selectedKeys={selectedKeys}
              size="sm"
              onSelectionChange={(keys) => {
                const keysArray = Array.from(keys);

                if (keysArray.length > 0) {
                  const modelId = keysArray[0];

                  if (modelId) {
                    const model = models.find((m) => m.id === modelId);

                    updateAgentParameters(agent_id, {
                      model: modelId,
                      max_tokens: model?.maxTokens || 200000,
                    });
                  }
                }
              }}
            >
              {(model) => (
                <SelectItem key={model.id} textValue={model.name}>
                  {model.name}
                </SelectItem>
              )}
            </Select>
          </div>
        );
      }

      if (param === "max_tokens") {
        const parsedValue = parseInt(String(value));
        const model = models.find((m) => m.id === agent.parameters.model);
        const maxValue = model ? model.maxTokens : 200000;

        return (
          <Slider
            key={uniqueKey}
            className="max-w-md"
            data-id={`param-${param}-slider`}
            isDisabled={!!runId}
            label={label}
            maxValue={maxValue}
            minValue={0}
            step={1000}
            value={parsedValue}
            onChange={(val) => updateAgentParameter(agent_id, param, String(val))}
          />
        );
      }

      if (
        typeof value === "boolean" ||
        (typeof value === "string" &&
          (value.toLowerCase() === "true" || value.toLowerCase() === "false"))
      ) {
        return (
          <Switch
            key={uniqueKey}
            aria-label={label}
            className="pt-2"
            data-id={`param-${param}-switch`}
            defaultSelected={String(value).toLowerCase() === "true"}
            id={`${agent_id}-${param}`}
            isDisabled={!!runId}
            size="sm"
            onValueChange={(checked) => updateAgentParameter(agent_id, param, String(checked))}
          >
            {label}
          </Switch>
        );
      }

      if (typeof value === "string") {
        if (value.length > 20) {
          return (
            <Textarea
              key={uniqueKey}
              className="pt-4"
              data-id={`param-${param}-textarea`}
              isDisabled={!!runId}
              label={label}
              size="sm"
              type="text"
              value={value}
              onChange={(e) => updateAgentParameter(agent_id, param, e.target.value)}
            />
          );
        }

        return (
          <div key={uniqueKey} data-id={`param-${param}-container`}>
            <span className="pt-2 text-small text-default-500">{label}</span>
            <Input
              className="pt-2"
              data-id={`param-${param}-input`}
              isDisabled={!!runId}
              size="sm"
              type="text"
              value={value}
              onChange={(e) => updateAgentParameter(agent_id, param, e.target.value)}
            />
          </div>
        );
      }

      if (typeof value === "number") {
        return (
          <Slider
            key={uniqueKey}
            className="max-w-md"
            data-id={`param-${param}-slider`}
            defaultValue={value}
            isDisabled={!!runId}
            label={label}
            maxValue={5000}
            minValue={0}
            step={1}
            onChange={(val) => updateAgentParameter(agent_id, param, String(val))}
          />
        );
      }

      return null;
    });

    return <React.Fragment key={`${agent_id}-parameters`}>{parameters}</React.Fragment>;
  };

  const renderAgentConfig = (agent_id: string, agent: AgentConfigInput) => {
    return (
      <div key={`${agent_id}-config`} data-id={`agent-${agent_id}-config`}>
        {renderTools(agent_id, agent)}
        {renderParameters(agent_id, agent)}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="flex w-full items-center justify-center p-4">
        <Spinner />
      </div>
    );
  }

  if (!workflow) {
    return null;
  }

  return (
    <div className="w-full flex flex-col pt-2" data-id="workflow-settings">
      {/* <span className="text-lg text-primary-700" data-id="workflow-settings-title">
        {t("Workflow Settings")}
      </span> */}
      {Object.entries(workflow.agents).map(([agent_id, agent]) => (
        <div key={agent_id} data-id={`agent-${agent_id}`}>
          <h3 className="text-md text-primary-700">
            {agent_id.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase())}
          </h3>
          {renderAgentConfig(agent_id, agent)}
        </div>
      ))}
    </div>
  );
};
